import { Clock, ImageIcon, Instagram as InstagramIcon, Trash2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useQueryClient } from "@tanstack/react-query"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface PostCardProps {
  post: {
    id: string
    image_url: string
    caption: string
    status: string
    created_at: string
    instagram_post_id?: string
  }
}

export const PostCard = ({ post }: PostCardProps) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const handleDelete = async () => {
    const { error } = await supabase
      .from("instagram_posts")
      .delete()
      .eq("id", post.id)

    if (error) {
      toast({
        title: "Erro ao deletar post",
        description: "Ocorreu um erro ao tentar deletar o post.",
        variant: "destructive",
      })
      return
    }

    toast({
      title: "Post deletado com sucesso",
      description: "O post foi removido apenas da plataforma, não do Instagram.",
    })

    // Atualiza a lista de posts
    queryClient.invalidateQueries({ queryKey: ["instagram-posts"] })
  }

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
      <div className="relative">
        {post.image_url ? (
          <img
            src={post.image_url}
            alt="Post"
            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
        )}
        <Badge 
          className={`absolute top-2 right-2 ${
            post.status === 'published' 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-yellow-500 hover:bg-yellow-600'
          }`}
        >
          {post.status === 'published' ? 'Publicado' : 'Pendente'}
        </Badge>
      </div>
      
      <CardContent className="p-4">
        <ScrollArea className="h-24">
          <p className="text-sm text-gray-600 leading-relaxed">
            {post.caption}
          </p>
        </ScrollArea>
        
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <time 
            dateTime={post.created_at}
            className="text-xs text-gray-500 flex items-center gap-1"
          >
            <Clock className="w-3 h-3" />
            {new Date(post.created_at).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </time>
          
          <div className="flex items-center gap-2">
            {post.instagram_post_id && (
              <a 
                href={`https://instagram.com/p/${post.instagram_post_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
              >
                <InstagramIcon className="w-3 h-3" />
                Ver no Instagram
              </a>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive/90 hover:bg-destructive/10 -mr-2"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Deletar Post</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja deletar este post? O post será removido apenas da plataforma, não do Instagram.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Deletar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}